import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { EvalCriterionDto } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { formatWeightPercent } from "../../../sourcing-events/buyer/wizard/utils/awardCriteriaStatus";
import { evalCriterionLabel, evalCriterionWeightPercent } from "./criterionLabel";
import { criterionTypeLabelKey } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";

interface Props {
  criteria: EvalCriterionDto[];
  readOnly: boolean;
  onSave: (criterionId: string, text: string) => Promise<void>;
}

export const CriterionRationaleSection = ({ criteria, readOnly, onSave }: Props) => {
  const { t } = useTranslation();

  if (criteria.length === 0) return null;

  return (
    <Accordion allowToggle borderColor="smBorder">
      <AccordionItem border="1px solid" borderColor="smBorder" rounded="lg" bg="smBackground" overflow="hidden">
        <AccordionButton>
          <Box flex="1" textAlign="left" fontWeight="semibold" color="smText">
            {t("Common assessment per criterion")}
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel>
          <Text fontSize="sm" color="smMuted" mb="4">
            {t("A shared assessment of each criterion across suppliers, used in the procurement record.")}
          </Text>
          <Stack spacing="4">
            {criteria.map((criterion) => (
              <RationaleField key={criterion.id} criterion={criterion} readOnly={readOnly} onSave={onSave} />
            ))}
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const RationaleField = ({
  criterion,
  readOnly,
  onSave,
}: {
  criterion: EvalCriterionDto;
  readOnly: boolean;
  onSave: (criterionId: string, text: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const [text, setText] = useState(criterion.commonRationale ?? "");

  // Resync when the server value changes (e.g. another evaluator edited it, or our own save resolved).
  useEffect(() => {
    setText(criterion.commonRationale ?? "");
  }, [criterion.commonRationale]);

  const commit = async () => {
    if (readOnly) return;
    if (text === (criterion.commonRationale ?? "")) return;
    try {
      await onSave(criterion.id, text);
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <FormControl>
      <Flex align="center" gap="2">
        <FormLabel fontSize="sm" mb="1">
          {evalCriterionLabel(criterion, t(criterionTypeLabelKey(criterion.type)))}
        </FormLabel>
        {evalCriterionWeightPercent(criterion) != null && (
          <Text fontSize="xs" color="smMuted">
            ({formatWeightPercent(evalCriterionWeightPercent(criterion) as number)}%)
          </Text>
        )}
      </Flex>
      <Textarea
        size="sm"
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        isReadOnly={readOnly}
        placeholder={readOnly ? "" : (t("Shared assessment across suppliers") ?? "")}
      />
    </FormControl>
  );
};
